<script>
import useVuelidate from '@vuelidate/core';
import {maxLength} from '@vuelidate/validators';
import DateDivider from '@views/threads/parts/DateDivider.vue';
import MessageBubble from '@views/threads/parts/MessageBubble.vue';
import ThreadMessagesHeader from '@views/threads/parts/ThreadMessagesHeader.vue';
import AkButtonActionV2 from '@components/v2/general/AkButtonActionV2.vue';
import MessageMediaMenu from '@views/threads/parts/MessageMediaMenu.vue';
import threadMixin from '@mixins/const/threadMixin';
import MessageMixin from '@mixins/messageMixin';
import threadService from '@services/threadService';
import threadRealtimeService, {REALTIME_EVENT_TYPE} from '@services/threadRealtimeService';
import moment from 'moment';
import store from '@store';
import AkPictureAccommodation from '@components/v2/general/AkPictureAccommodation.vue';
import AkShimmer from '@components/v2/general/AkShimmer.vue';
import AkDialog from '@components/general/AkDialog.vue';

export default {
  name: 'ThreadMessages',
  mixins: [threadMixin, MessageMixin],
  components: {
    AkDialog,
    AkShimmer,
    AkPictureAccommodation,
    AkButtonActionV2,
    ThreadMessagesHeader,
    MessageBubble,
    DateDivider,
    MessageMediaMenu,
  },
  props: {
    modelValue: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    archived: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'update:modelValue',
    'message-sent',
    'archive',
    'unarchive',
    'favorite',
    'assign',
    'unassign',
    'openDetailsSidebar',
  ],
  setup() {
    return {v$: useVuelidate()};
  },
  data() {
    return {
      newMessage: {
        text: '',
        media: [],
      },
      sending: false,
      isScrolled: true,
      typingSubscription: null,
      typingUsers: {},
      lastTypingPublishAt: 0,
      isAtBottom: true,
      newMessageCount: 0,
      reactionsMessageId: null,
    };
  },
  validations() {
    return {
      newMessage: {
        text: {maxLengthValue: maxLength(2000)},
      },
    };
  },
  mounted() {
    this.typingSubscription = threadRealtimeService.subscribeToThread(this.$route.params.threadId, {
      [REALTIME_EVENT_TYPE.TYPING]: (payload) => this.onTypingEvent(payload),
    });
    this.$nextTick(() => this.$refs.messageList?.addEventListener('scroll', this.onScroll));
  },
  beforeUnmount() {
    if (this.typingSubscription) this.typingSubscription.unsubscribe();
    Object.values(this.typingUsers).forEach((u) => clearTimeout(u.timeout));
    this.$refs.messageList?.removeEventListener('scroll', this.onScroll);
  },
  computed: {
    conversation: {
      get() {
        if (!this.modelValue) return null;
        return {...this.modelValue.conversation, guestNames: this.modelValue.conversation.guests.map((g) => g.name)};
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
    supportedReactions() {
      return this.conversation?.supportedReactions ?? [];
    },
    // Message dont on affiche le détail des réactions : relu dans la conversation pour suivre les mises à jour temps réel
    reactionsMessage() {
      if (!this.conversation || !this.reactionsMessageId) return null;
      return this.conversation.messages.find((m) => m.id === this.reactionsMessageId) ?? null;
    },
    lastMessage() {
      if (!this.conversation) return null;
      const messages = this.conversation.messages;
      return messages[messages.length - 1];
    },
    messagesByDay() {
      if (!this.conversation) return [];
      const groups = {};
      for (const message of this.conversation.messages) {
        const key = moment.utc(message.sentMoment).local().toDate().toDateString();
        if (!groups[key]) groups[key] = {date: moment.utc(message.sentMoment).local().toDate(), messages: []};
        groups[key].messages.push(message);
      }
      return Object.values(groups).map((group) => {
        const messages = group.messages.map((msg, i) => {
          const prev = group.messages[i - 1];
          const next = group.messages[i + 1];
          const isLastInCluster = !next || next.authorDisplay !== msg.authorDisplay || next.sent !== msg.sent;
          const isFirstInCluster = !prev || prev.authorDisplay !== msg.authorDisplay || prev.sent !== msg.sent;
          return {...msg, isLastInCluster, isFirstInCluster};
        });
        return {...group, messages};
      });
    },
    dayLabel() {
      return (date) => {
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        if (date.toDateString() === today.toDateString()) return "Aujourd'hui";
        if (date.toDateString() === yesterday.toDateString()) return 'Hier';
        return date.toLocaleDateString('fr-FR', {weekday: 'long', day: 'numeric', month: 'long'});
      };
    },
    moment() {
      return moment;
    },
    typingLabel() {
      const names = Object.values(this.typingUsers)
        .map((u) => u.displayName)
        .filter(Boolean);
      if (!names.length) return null;
      return names.length === 1 ? `${names[0]} est en train d'écrire` : `${names.join(', ')} sont en train d'écrire`;
    },
    threadType() {
      return this.modelValue?.typeDetails.type;
    },
    statusLabel() {
      if (this.isReservationConfirmed) return 'Réservation confirmée';
      if (this.isSpecialOfferActive) return 'Offre envoyée';
      if (this.isSpecialOfferExpired) return 'Offre expirée';
      if (this.isSpecialOfferVoided) return 'Offre annulée';
      if (this.isReservationQuery) return 'Demande de réservation';
      if (this.isReservationDenied) return 'Demande de réservation refusée';
      if (this.isReservationRetracted) return 'Demande de réservation annulée';
      if (this.isReservationCancelled) return 'Réservation annulée';
      if (this.isReservationExpired) return 'Demande de réservation expirée';
      if (this.isInquiry) return "Demande d'informations";
      if (this.isInquiryDeclined) return "Demande d'informations refusée";
      if (this.isInquiryExpired) return "Demande d'informations expirée";
      return '';
    },
  },
  methods: {
    autoGrowMessageInput() {
      const elem = this.$refs.messageInput;
      elem.style.height = 'auto';
      elem.style.height = `${elem.scrollHeight}px`;
    },
    scrollToBottom(smooth = true) {
      this.$nextTick(() => {
        const elem = this.$refs.messageList;
        elem.scrollTo({top: elem.scrollHeight, behavior: smooth ? 'smooth' : 'instant'});
      });
    },
    sendMessage() {
      if (this.sending) return;
      if (this.v$.newMessage.text.$invalid) return;
      const text = this.newMessage.text.trim();
      const hasText = text.length > 0;
      const hasMedia = this.newMessage.media.length > 0;
      if (!hasText && !hasMedia) return;

      const authorDisplay = store.state.userInformation.firstName + ' ' + store.state.userInformation.lastName;

      const base = {
        type: 'sent',
        sent: true,
        authorDisplay: authorDisplay,
        authorPictureUrl: null,
        platform: 'GAPP',
        sentMoment: moment.utc().toDate(),
        sending: true,
      };
      // Généré côté FO, renvoyé tel quel par le push MESSAGE_ADDED : permet de réconcilier ce message optimiste avec
      // sa version poussée par le WebSocket si celle-ci arrive avant la réponse REST (course entre les deux canaux).
      const clientMessageId = hasText ? `${Date.now()}-${Math.random().toString(36).slice(2)}` : null;
      const pending = [];
      if (hasText) pending.push({...base, translatedContent: text, clientMessageId});
      this.newMessage.media.forEach((m) => {
        pending.push({...base, translatedContent: '', mediaUrl: m.url, mediaType: m.type});
      });
      // On envoie toujours depuis le bas : la file d'attente "nouveaux messages" ne doit pas se déclencher ici.
      this.isAtBottom = true;
      this.newMessageCount = 0;
      pending.forEach((m) => this.conversation.messages.push(m));
      pending.forEach((m, i) => {
        const index = this.conversation.messages.indexOf(m);
        if (index !== -1) pending[i] = this.conversation.messages[index];
      });
      this.scrollToBottom();

      const files = this.newMessage.media.map((m) => m.file);
      const mediaToRevoke = this.newMessage.media;
      this.newMessage = {text: '', media: []};
      this.sending = true;
      this.$nextTick(this.autoGrowMessageInput);

      this.attemptSendMessage({
        threadId: Number(this.$route.params.threadId),
        text: hasText ? text : null,
        files,
        clientMessageId,
        pending,
        authorDisplay,
        hasMedia,
        mediaToRevoke,
      }).finally(() => {
        this.sending = false;
      });
    },
    attemptSendMessage(payload) {
      const {threadId, text, files, clientMessageId, pending, authorDisplay, hasMedia, mediaToRevoke} = payload;
      pending.forEach((m) => {
        m.sending = true;
        m.hasError = false;
      });
      return threadService
        .sendMessage(threadId, text, files, clientMessageId)
        .then((createdMessages) => {
          pending.forEach((m) => {
            const index = this.conversation.messages.indexOf(m);
            if (index !== -1) this.conversation.messages.splice(index, 1);
          });
          createdMessages.forEach((created, i) => {
            // Le push WEBSOCKET a pu déjà remplacer le message optimiste (course gagnée par le WS) : ne pas dupliquer.
            if (this.conversation.messages.some((m) => m.id === created.id)) return;
            this.conversation.messages.push({
              ...created,
              type: 'sent',
              authorPictureUrl: null,
              mediaUrl: pending[i]?.mediaUrl,
              mediaType: pending[i]?.mediaType,
            });
          });
          this.scrollToBottom();
          mediaToRevoke.forEach((m) => URL.revokeObjectURL(m.url));
          const lastMedia = mediaToRevoke.length ? mediaToRevoke[mediaToRevoke.length - 1] : null;
          this.$emit('message-sent', {
            authorDisplay: authorDisplay,
            message: hasMedia ? null : text,
            type: hasMedia ? lastMedia.type : 'text',
            moment: moment.utc().toDate(),
          });
        })
        .catch((e) => {
          pending.forEach((m) => {
            m.sending = false;
            m.hasError = true;
            m.retry = () => this.attemptSendMessage(payload);
          });
          this.addError(
            e.response?.status === 417
              ? 'Impossible d’envoyer le message.'
              : 'Une erreur est survenue lors de l’envoi du message.',
          );
        });
    },
    resendMessage(message) {
      if (message.resending) return;
      if (message.retry) {
        message.resending = true;
        message.retry().finally(() => {
          message.resending = false;
        });
        return;
      }
      const target = this.conversation.messages.find((m) => m.id === message.id);
      if (!target) return;
      target.resending = true;
      threadService
        .resendMessage(Number(this.$route.params.threadId), message.id)
        .then((updated) => {
          Object.assign(target, updated);
          // Le renvoi date le message à maintenant : il doit redevenir le plus récent de la liste.
          const index = this.conversation.messages.indexOf(target);
          if (index !== -1) this.conversation.messages.splice(index, 1);
          this.conversation.messages.push(target);
          this.scrollToBottom();
        })
        .catch(() => this.addError('Impossible de renvoyer le message.'))
        .finally(() => {
          target.resending = false;
        });
    },
    reactToMessage(message, emoji) {
      const target = this.conversation.messages.find((m) => m.id === message.id);
      if (!target) return;
      threadService
        .setReaction(Number(this.$route.params.threadId), message.id, emoji)
        .then((reactions) => {
          target.reactions = reactions;
        })
        .catch(() => this.addError(this.$t('threads.reactions.set_error')));
    },
    showReactions(message) {
      this.reactionsMessageId = message.id;
      this.$refs.reactionsDialog.show();
    },
    removeReaction(message) {
      const target = this.conversation.messages.find((m) => m.id === message?.id);
      if (!target) return;
      threadService
        .removeReaction(Number(this.$route.params.threadId), target.id)
        .then((reactions) => {
          target.reactions = reactions;
          if (!reactions.length && this.reactionsMessageId === target.id) this.$refs.reactionsDialog.hide();
        })
        .catch(() => this.addError(this.$t('threads.reactions.remove_error')));
    },
    removeMedia(index) {
      URL.revokeObjectURL(this.newMessage.media[index].url);
      this.newMessage.media.splice(index, 1);
    },
    onSelectTemplate(content) {
      this.newMessage.text = this.newMessage.text?.trim() ? `${this.newMessage.text}\n${content}` : content;
      this.$nextTick(() => {
        this.autoGrowMessageInput();
        this.$refs.messageInput?.focus();
      });
    },
    openDetailsSidebar() {
      this.$emit('openDetailsSidebar');
    },
    onMessageInput() {
      this.autoGrowMessageInput();
      this.notifyTyping();
    },
    notifyTyping() {
      // Simple throttle : au plus un envoi toutes les 2s tant que l'utilisateur tape, la réception se charge d'effacer
      // l'indicateur après quelques secondes de silence (cf. onTypingEvent).
      const now = Date.now();
      if (now - this.lastTypingPublishAt < 2000) return;
      this.lastTypingPublishAt = now;
      threadRealtimeService.sendTyping(this.$route.params.threadId);
    },
    onTypingEvent({userId, displayName, clientId}) {
      if (clientId === threadRealtimeService.clientId) return;
      if (this.typingUsers[userId]) clearTimeout(this.typingUsers[userId].timeout);
      const updated = {...this.typingUsers};
      updated[userId] = {
        displayName,
        timeout: setTimeout(() => {
          const cleared = {...this.typingUsers};
          delete cleared[userId];
          this.typingUsers = cleared;
        }, 5000),
      };
      this.typingUsers = updated;
    },
    onScroll() {
      const el = this.$refs.messageList;
      if (!el) return;
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      this.isAtBottom = distanceFromBottom < 80;
      if (this.isAtBottom) this.newMessageCount = 0;
    },
    jumpToBottom() {
      this.isAtBottom = true;
      this.newMessageCount = 0;
      this.scrollToBottom();
    },
  },
  watch: {
    modelValue(newVal) {
      if (newVal) {
        this.isAtBottom = true;
        this.newMessageCount = 0;
        this.scrollToBottom(false);
        if (!this.$isMobile()) this.$refs.messageInput.focus();
      }
    },
    immediate: true,
    deep: true,
    'conversation.messages.length'(newLength, oldLength) {
      // Ne réagit qu'aux ajouts (une suppression -- ex: retrait d'un message optimiste avant remplacement -- ne
      // doit ni scroller ni compter). Le cas "envoi par moi-même" est déjà couvert : isAtBottom est forcé à true
      // avant de pousser le message optimiste, cf. sendMessage().
      if (oldLength === undefined || newLength <= oldLength) return;
      if (this.isAtBottom) {
        this.scrollToBottom();
      } else {
        this.newMessageCount += newLength - oldLength;
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-full divide-y divide-grey-100">
    <thread-messages-header
      v-if="!loading"
      :conversation="conversation"
      :thread-id="Number($route.params.threadId)"
      :archived="archived"
      :favorite="modelValue?.favorite"
      :assigned-employee="modelValue?.assignedEmployee"
      @archive="$emit('archive')"
      @unarchive="$emit('unarchive')"
      @favorite="$emit('favorite')"
      @assign="$emit('assign', $event)"
      @unassign="$emit('unassign')" />
    <div v-else class="w-full flex p-4 items-center gap-4 h-[80px]">
      <div class="flex shrink-0">
        <AkShimmer :loading="true" width="44px" height="44px" border-radius="9999px" />
      </div>
      <span class="grow">
        <AkShimmer :loading="true" width="110px" height="18px" />
      </span>
      <div class="flex gap-2 items-center shrink-0">
        <AkShimmer :loading="true" width="102px" height="40px" border-radius="8px" />
        <AkShimmer :loading="true" width="40px" height="40px" border-radius="9999px" />
      </div>
    </div>
    <div class="flex w-full relative flex-col flex-1 min-h-0 overflow-y-auto gap-5 md:px-8 px-4 pt-6" ref="messageList">
      <template v-if="!loading">
        <button
          @click="openDetailsSidebar"
          class="floating-action-bar bg-white/90 backdrop-blur-sm active:bg-grey-50/90 z-10 xl:hidden flex gap-3 sticky md:-top-2 -top-4 md:-mx-4 -mx-2 md:-mt-2 -mt-4 left-2 right-2 p-2 pr-3 border border-grey-100 hover:border-grey-200 rounded-xl items-center cursor-pointer">
          <AkPictureAccommodation
            :id="modelValue.accommodation.id"
            :md5="modelValue.accommodation.pictureMd5"
            :height="44"
            class="max-w-[44px]"
            disabled-link
            small
            radius="rounded-md"
            cover />
          <div class="flex text-left flex-col gap-0.5">
            <span class="text-sm text-grey-500">{{ statusLabel }}</span>
            <span class="text-grey-900 font-medium">
              <template v-if="isReservationConfirmed">Voir la réservation</template>
              <template v-else-if="isSpecialOfferVoided || isSpecialOfferExpired || isSpecialOfferActive">
                Voir l'offre
              </template>
              <template v-else>Voir la demande</template>
            </span>
          </div>
          <div class="min-h-8 min-w-8 bg-primary flex items-center justify-center rounded-full ml-auto">
            <i class="ga-icon ga-chevron_right text-white text-md" />
          </div>
        </button>
        <span class="self-center text-sm text-grey-500 font-medium text-center">
          Début de votre conversation avec {{ title }}
        </span>
        <div class="flex flex-col gap-1" v-for="group in messagesByDay" :key="group.date.toDateString()">
          <date-divider :label="dayLabel(group.date)" />
          <message-bubble
            v-for="message in group.messages"
            :key="moment.utc(message.sentMoment).toDate().getTime()"
            :message="message"
            :is-last-in-cluster="message.isLastInCluster"
            :is-first-in-cluster="message.isFirstInCluster"
            :is-last-message="message.id === lastMessage.id"
            :guests="conversation.guests"
            :last-messages-read="conversation.lastMessagesRead"
            :supported-reactions="supportedReactions"
            @resend="resendMessage"
            @react="reactToMessage"
            @remove-reaction="removeReaction"
            @show-reactions="showReactions" />
        </div>
      </template>
      <template v-else>
        <div class="flex flex-col gap-1 mb-2">
          <div class="flex md:mr-[min(80px,15%)] pr-6 py-3 px-4">
            <AkShimmer :loading="true" width="220px" height="18px" />
          </div>
        </div>
        <div class="flex flex-col gap-1 mb-2">
          <div class="flex md:mr-[min(80px,15%)] pr-6 py-3 px-4">
            <AkShimmer :loading="true" width="150px" height="18px" />
          </div>
          <div class="flex items-center gap-2">
            <AkShimmer :loading="true" width="28px" height="28px" border-radius="9999px" />
            <AkShimmer :loading="true" width="140px" height="16px" />
          </div>
        </div>
        <div class="flex flex-col gap-1">
          <div class="flex md:ml-[min(80px,15%)] self-end pl-6 py-3 px-4">
            <AkShimmer :loading="true" width="190px" height="18px" />
          </div>
          <div class="flex items-center gap-2 flex-row-reverse">
            <AkShimmer :loading="true" width="28px" height="28px" border-radius="9999px" />
            <AkShimmer :loading="true" width="140px" height="16px" />
          </div>
        </div>
      </template>
      <div class="grow relative md:pt-7">
        <Transition name="typing-fade">
          <span
            v-if="typingLabel"
            class="absolute bottom-0 flex items-end gap-2 text-sm text-grey-500 italic right-0 -mb-3 leading-[9px]">
            {{ typingLabel }}
            <span class="flex gap-0.5 items-center">
              <span class="typing-dot-sm" />
              <span class="typing-dot-sm" />
              <span class="typing-dot-sm" />
            </span>
          </span>
        </Transition>
      </div>
      <div
        class="sticky bottom-0 z-10 -mx-4 md:px-4 md:pb-4 px-2 pb-2 !border-t-0 bg-gradient-to-t from-white/90 to-white/0 backdrop-blur-sm">
        <Transition name="typing-fade">
          <button
            v-if="newMessageCount > 0"
            @click="jumpToBottom"
            class="absolute -top-12 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-white shadow-md border border-grey-100 rounded-full pl-3 pr-2 py-1.5 text-sm font-medium text-grey-900 hover:bg-grey-50">
            <span>
              {{ newMessageCount }} nouveau{{ newMessageCount > 1 ? 'x' : '' }} message{{
                newMessageCount > 1 ? 's' : ''
              }}
            </span>
            <i class="ga-icon ga-chevron_down text-md" />
          </button>
        </Transition>
        <div class="w-full rounded-lg border border-grey-100 shrink-0 overflow-hidden bg-white">
          <div
            class="flex flex-col md:p-4 p-2 pl-2.5 !pb-1 gap-2"
            :class="newMessage.media.length ? 'md:min-h-[180px]' : 'md:min-h-[120px]'">
            <div v-if="newMessage.media.length" class="flex gap-2 overflow-x-auto py-1 -my-1">
              <div v-for="(item, i) in newMessage.media" :key="i" class="relative h-16 w-16 shrink-0 bg-grey-50 group">
                <img
                  v-if="item.type === 'image'"
                  :src="item.url"
                  alt=""
                  class="h-full w-full object-cover rounded-lg" />
                <video v-else :src="item.url" class="h-full w-full object-cover" />
                <button
                  class="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-grey-900/70 opacity-0 group-hover:opacity-100 text-white flex items-center justify-center"
                  @click="removeMedia(i)">
                  <span class="ga-icon ga-supprimer text-[11px] font-bold" />
                </button>
              </div>
            </div>
            <div class="flex md:flex-col grow md:items-start items-center gap-2" id="mobileInputBar">
              <MessageMediaMenu
                v-if="$isMobile()"
                v-model:media="newMessage.media"
                :thread-id="Number($route.params.threadId)"
                class="order-1"
                @select-template="onSelectTemplate" />
              <textarea
                ref="messageInput"
                rows="1"
                placeholder="Écrire un message"
                class="grow shrink-0 md:w-full resize-none md:max-h-20 max-h-12 h-fit self-center md:py-0 py-3 order-1 overflow-auto"
                v-model="newMessage.text"
                @input="onMessageInput"
                @keydown.ctrl.enter.prevent="$refs.sendBtn.click()" />
              <button
                v-if="$isMobile()"
                :disabled="sending"
                class="font-medium !h-12 !w-12 focus-visible:bg-primary-700 btn-primary order-1"
                @click="sendMessage">
                <i class="ga-icon ga-send text-3xl"></i>
              </button>
            </div>
            <div
              class="hidden md:flex justify-end text-sm font-medium"
              :class="{
                'text-grey-500': newMessage.text.length < 1800,
                'text-orange-500': newMessage.text.length >= 1800 && newMessage.text.length <= 2000,
                'text-red-500': newMessage.text.length > 2000,
              }">
              {{ newMessage.text.length }}/2000
            </div>
          </div>

          <div class="hidden md:flex pr-2 pl-3 py-2.5 gap-2 items-center">
            <MessageMediaMenu
              v-model:media="newMessage.media"
              :thread-id="Number($route.params.threadId)"
              class="mr-auto"
              @select-template="onSelectTemplate" />
            <AkButtonActionV2
              ref="sendBtn"
              label="Répondre"
              :disabled="sending || !newMessage.text?.trim().length || v$.newMessage.text.$invalid"
              class="font-medium !h-12 !px-4 focus-visible:bg-primary-700 transition-background duration-100"
              @click="sendMessage" />
          </div>
        </div>
      </div>
    </div>

    <AkDialog
      ref="reactionsDialog"
      :cancel-label="$t('threads.reactions.close')"
      :show-validate-button="false"
      :title="$t('threads.reactions.title')"
      width="400px"
      @hide="reactionsMessageId = null">
      <ul v-if="reactionsMessage" class="flex flex-col gap-3">
        <li v-for="reaction in reactionsMessage.reactions" :key="reaction.id" class="flex items-center gap-3">
          <span class="text-xl">{{ reaction.emoji }}</span>
          <span class="font-medium text-grey-900">{{ reaction.authorDisplay }}</span>
          <button
            v-if="reaction.host"
            class="ml-auto font-medium text-sm text-red-600 hover:text-red-700 focus-visible:text-red-700 underline"
            @click="removeReaction(reactionsMessage)">
            {{ $t('threads.reactions.remove') }}
          </button>
        </li>
      </ul>
    </AkDialog>
  </div>
</template>

<style scoped>
.typing-fade-enter-active,
.typing-fade-leave-active {
  transition: opacity 0.2s ease;
}

.typing-fade-enter-from,
.typing-fade-leave-to {
  opacity: 0;
}

.typing-dot-sm {
  display: inline-block;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: #9ca3af;
  animation: typing-bounce-sm 1.2s infinite ease-in-out;
}
.typing-dot-sm:nth-child(2) {
  animation-delay: 0.2s;
}
.typing-dot-sm:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing-bounce-sm {
  0%,
  60%,
  100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  30% {
    transform: translateY(-3px);
    opacity: 1;
  }
}

.floating-action-bar {
  @apply shadow-sm;
  box-shadow:
    var(--tw-ring-offset-shadow, 0 0 #0000),
    var(--tw-ring-shadow, 0 0 #0000),
    var(--tw-shadow),
    inset 0 1px 3px #fff !important;
}
</style>
